import { ProductTypeFinderDialogComponent } from "@/inventory/product-type-finder-dialog.component";
import { AgFns, GridOptionsExt, RowSelectionMultiple, RowSelectionMultipleCheckbox, RowSelectionSingle } from "@/shared/ag-fns";
import { UtilFns } from "@/shared/util-fns";
import { ColDef, GetRowIdParams, GridOptions, GridReadyEvent } from "@ag-grid-community/core";
import { Component } from "@angular/core";
import { AccountFinderDialogComponent } from "app/admin/account-finder-dialog.component";
import { ProductFinderDialogComponent } from "app/inventory/product-finder-dialog.component";
import {
  Account,
  Feature,
  FeatureChoice,
  Manufacturer,
  ManufacturerProductTypeConfig,
  PoDetail,
  PoDetailType,
  PoHeader,
  Product,
  ProductType,
} from "app/model/entities/entity-model";
import { PoDetailTypeCode } from "app/model/enums/po-detail-type-code";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { EntityFinderDialog } from "app/shared/entity-finder.dialog";
import _ from "lodash";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DateFns } from "@/shared/date-fns";
import { ManufacturerProductTypeFinderDialogComponent } from "@/inventory/manufacturer-product-type-finder-dialog.component";

export interface AddPodsDialogParams extends DialogParams {
  poHeader: PoHeader;
}

@Component({
  templateUrl: "./add-pods-dialog.component.html",
  standalone: false,
})
export class AddPodsDialogComponent extends DialogBaseComponent<AddPodsDialogParams> {
  poHeader: PoHeader;

  podGridOptions!: GridOptionsExt;
  manufacturer: Manufacturer;
  manufacturerProductTypeConfig?: ManufacturerProductTypeConfig;
  productType: ProductType;
  account: Account;

  features: Feature[] = [];
  selectedFeature?: Feature;

  featureChoices: FeatureChoice[] = [];
  selectedFeatureChoices: FeatureChoice[] = [];

  // key is featureId
  savedFeatureChoiceMap = new Map<Feature, FeatureChoice[]>();
  pods: PoDetail[] = [];

  poDetailTypes: PoDetailType[];
  allAccounts: Account[];
  matchingAccounts: Account[];

  constructor(
    public config: DynamicDialogConfig<AddPodsDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
  ) {
    super(config, dialogRef, domainService);

    Object.assign(this.config, {
      width: "1000px",
      height: "800px",
    });
    if (this.config.header == null) {
      this.config.header = "Create Stock Purchase Order Details";
    }
    this.prepare();
  }

  async prepare() {
    this.poHeader = this.params.poHeader;
    this.manufacturer = this.poHeader.manufacturer;

    this.poDetailTypes = await this.dbQueryService.cacheAndGetAll(PoDetailType);
    this.allAccounts = await this.dbQueryService.getReissuanceAccounts();

    const nullAccount = <Account>{ accountName: "[None]" };
    this.allAccounts.unshift(nullAccount);

    this.podGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onPodGridReady,
      },
      {
        detailProperty: "manufacturerProductTypeConfig.manufacturerProductTypeConfigAddons",
      },
    );
    this.updatePodMasterDetail(this.podGridOptions);

    this.isPageReady = true;
  }

  onPodGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      {
        headerName: "Manuf Config Name",
        field: "manufacturerProductTypeConfig.configName",
        filter: "agTextColumnFilter",
        cellRenderer: "agGroupCellRenderer",
      },
      {
        headerName: "Manuf Config Description",
        field: "manufacturerProductTypeConfig.configDescription",
        filter: "agTextColumnFilter",
      },
      { headerName: "Description", field: "product.productType.description" },
      { headerName: "SKU", field: "product.productType.style" },

      { headerName: "Features", field: "product.featureChoicesExtract" },
      {
        headerName: "Order Qty",
        field: "orderQty",
        type: "numericColumn",
        editable: true,
        maxWidth: 100,
        cellClass: "cell-editable",
      },
      {
        headerName: "Ship Date",
        field: "expectedShipDate",
        editable: true,
        cellClass: "cell-editable",
        filter: "agDateColumnFilter",
        cellEditor: "agDateCellEditor",
      },
      {
        ...AgFns.createButtonProps("", this.deletePod.bind(this), {
          label: "Delete",
        }),
      },
    ];
    const sortFields = [
      "product.productType.manufacturer.name",
      "product.productType.description",
      "product.productType.style",
      "product.getSortOrder()",
    ];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" as const };
    });

    AgFns.initGrid(event, colDefs, sortModel);
  }

  updatePodMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: "Addon", field: "addon.nameAndLocation" },
      { headerName: "Addl. Info", field: "additionalInfo" },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);

    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      refreshStrategy: "everything",

      getDetailRowData: (params) => {
        const pod = params.data as PoDetail;
        const mptcAddons = pod.manufacturerProductTypeConfig.manufacturerProductTypeConfigAddons;
        params.successCallback(mptcAddons);
      },
    };
  }

  getPngDialogService() {
    return this.domainService.pngDialogService;
  }

  searchAccounts(query: string) {
    this.matchingAccounts = this.allAccounts.filter((a) => a.accountName.toLowerCase().indexOf(query.toLowerCase()) > -1);
  }

  async lookupProductType() {
    const r = await EntityFinderDialog.open(ProductTypeFinderDialogComponent, this, {
      manufacturer: this.poHeader.manufacturer,
      rowSelection: RowSelectionSingle,
    });
    if (r.length == 0) return false;
    this.manufacturerProductTypeConfig = undefined;
    this.productType = r[0];
    return await this.lookupProduct();
  }

  async lookupMptcs() {
    const r = await EntityFinderDialog.open(ManufacturerProductTypeFinderDialogComponent, this, {
      manufacturer: this.manufacturer,
      rowSelection: RowSelectionSingle,
    });
    if (r.length == 0) return;
    this.manufacturerProductTypeConfig = r[0];
    this.productType = this.manufacturerProductTypeConfig.productType;
    return await this.lookupProduct();
  }

  async lookupProduct() {
    const prods = await EntityFinderDialog.open(ProductFinderDialogComponent, this, {
      productType: this.productType,
      rowSelection: RowSelectionMultiple,
    });
    if (prods.length == 0) return false;
    const leadDays = this.manufacturer.leadTimeDays ?? 0;
    prods.forEach((prod) => {
      const podStruct = <PoDetail>{};
      const now = new Date();
      podStruct.poHeaderId = this.poHeader.id;
      // will be null if NOT account-owned.
      podStruct.accountId = this.account?.id;
      podStruct.productId = prod.id;
      // may be null
      podStruct.manufacturerProductTypeConfigId = this.manufacturerProductTypeConfig?.id;
      podStruct.origProductId = prod.id;

      podStruct.poDetailTypeId = PoDetailTypeCode.Stock;
      podStruct.orderQty = 1;

      const pod = this.uow.createEntity(PoDetail, podStruct);
      this.pods.push(pod);
    });
    this.updateColVisibility();
    AgFns.refreshGrid(this.podGridOptions, this.pods);
  }

  updateColVisibility() {
    const hasAnyMptcs = this.pods.some((p) => p.manufacturerProductTypeConfigId != null);
    this.podGridOptions.apiX.setColumnsVisible(["manufacturerProductTypeConfig.configName"], hasAnyMptcs);
    this.podGridOptions.apiX.setColumnsVisible(["manufacturerProductTypeConfig.configDescription"], hasAnyMptcs);
  }

  async lookupAccount() {
    const r = await EntityFinderDialog.open(AccountFinderDialogComponent, this, {
      shouldShowReissueAccountsOnly: true,
    });
    if (r.length == 0) return false;
    const account = r[0];
    this.account = account;
    return this.account != null;
  }

  async deletePod(pod: PoDetail) {
    pod.entityAspect.rejectChanges();
    this.pods = this.pods.filter((p) => p.id !== pod.id);
    this.updateColVisibility();
    AgFns.refreshGrid(this.podGridOptions, this.pods);
  }

  async save() {
    await this.dbSaveService.saveSelectedChanges(this.pods);
    this.dialogRef.close(true);
  }

  cancel() {
    this.uow.rollback();
    this.dialogRef.close(false);
  }
}
